<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="560"
    persistent
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          left
          :color="overallColor"
        >
          {{ icons.mdiPrinterCheck }}
        </v-icon>
        プリンタ診断
        <v-spacer />
        <v-btn
          icon
          small
          :disabled="isRunning"
          @click="onClose"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <!-- 接続情報サマリ -->
        <div class="text-caption text--secondary mb-1">
          接続先:
          <span class="text--primary">{{ connectionResult ? (connectionResult.ipAddress || '未設定') : '—' }}</span>
          <span
            v-if="connectionResult && connectionResult.port"
            class="ml-1"
          >:{{ connectionResult.port }}</span>
        </div>
        <div class="text-caption text--secondary mb-1">
          接続経路:
          <span class="text--primary">{{ connectionSourceText }}</span>
        </div>
        <div class="text-caption text--secondary mb-1">
          直近の応答status:
          <span class="text--primary">{{ latestStatusHex }}</span>
        </div>
        <div class="text-caption text--secondary mb-3">
          最終診断: {{ lastRunAtText }}
        </div>

        <div
          v-if="isRunning"
          class="py-6"
        >
          <div class="d-flex align-center justify-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="28"
              class="mr-3"
            />
            {{ runningMessage }}
          </div>
          <v-alert
            v-if="showLongWaitHint"
            type="warning"
            dense
            text
            class="mt-4 mb-0"
          >
            応答に時間がかかっています。プリンタ本体が反応していない可能性があります。
            <strong>プリンタの電源を一度切って入れ直してから、再度診断してみてください。</strong>
          </v-alert>
        </div>

        <template v-else>
          <!-- 接続結果 -->
          <v-alert
            v-if="connectionResult"
            :type="connectionResult.ok ? 'success' : 'error'"
            dense
            text
            class="mb-3"
          >
            <div class="font-weight-bold">
              接続: {{ connectionResult.ok ? 'OK' : 'NG' }}
            </div>
            <div
              v-if="!connectionResult.ok && connectionResult.offline"
              class="text-caption mt-1"
            >
              ネットワーク未接続（この端末がインターネットから切断されています）
            </div>
            <div
              v-else-if="!connectionResult.ok && connectionResult.error"
              class="text-caption mt-1"
            >
              {{ connectionResult.error }}
            </div>
            <div
              v-if="!connectionResult.ok && !connectionResult.offline && connectionResult.printerReachable !== null && connectionResult.printerReachable !== undefined"
              class="text-caption mt-1"
            >
              プリンタ本体への疎通:
              <span :class="connectionResult.printerReachable ? 'success--text' : 'error--text'">
                {{ connectionResult.printerReachable ? '到達OK（プリンタ本体は生きているが印刷サービスが反応していません）' : '到達不可（プリンタに届いていません）' }}
              </span>
            </div>
            <div
              v-if="!connectionResult.ok && connectionResult.certError"
              class="text-caption mt-1 error--text"
            >
              SSL証明書エラーを検知しました（NET::ERR_CERT_AUTHORITY_INVALID の可能性）
            </div>
          </v-alert>

          <!-- テスト印刷結果（接続成功時のみ意味があるので接続OK時のみ表示） -->
          <v-alert
            v-if="printResult && connectionOk"
            :type="printResult.ok ? 'success' : 'warning'"
            dense
            text
            class="mb-3"
          >
            <div class="font-weight-bold">
              テスト印刷: {{ printResult.ok ? 'OK' : 'NG' }}
            </div>
            <div
              v-if="!printResult.ok && printResult.code"
              class="text-caption mt-1"
            >
              エラーコード: {{ printResult.code }}
            </div>
          </v-alert>

          <!-- ステータス取得のみ結果（同じく接続OK時のみ） -->
          <v-alert
            v-if="statusOnlyResult && !printResult && connectionOk"
            :type="statusOnlyResult.ok ? 'success' : 'warning'"
            dense
            text
            class="mb-3"
          >
            <div class="font-weight-bold">
              ステータス取得: {{ statusOnlyResult.ok ? 'OK' : 'NG' }}
            </div>
            <div
              v-if="statusOnlyResult.source === 'timeout'"
              class="text-caption mt-1"
            >
              プリンタからの応答がありません
            </div>
          </v-alert>

          <!-- 各ASBビットのチェックリスト（接続失敗時はプリンタ状態が取れないので非表示） -->
          <v-list
            v-if="currentChecks.length && connectionOk"
            dense
            class="py-0"
          >
            <v-list-item
              v-for="check in currentChecks"
              :key="check.key"
              class="px-2"
            >
              <v-list-item-icon class="my-2 mr-2">
                <v-icon :color="checkIconColor(check)">
                  {{ checkIcon(check) }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-body-2">
                  {{ check.label }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ check.text }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- 対処方法 -->
          <v-alert
            v-if="actionMessage"
            type="info"
            dense
            text
            class="mt-3 mb-0"
          >
            {{ actionMessage }}
          </v-alert>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3 flex-wrap">
        <v-btn
          color="primary"
          :loading="isRunning"
          :disabled="isRunning"
          @click="runDiagnostic"
        >
          <v-icon left>
            {{ icons.mdiRefresh }}
          </v-icon>
          {{ lastRunAt ? '再診断' : '診断開始' }}
        </v-btn>
        <v-btn
          text
          :disabled="isRunning"
          @click="runStatusOnly"
        >
          <v-icon left>
            {{ icons.mdiAlertOctagonOutline }}
          </v-icon>
          ステータスのみ
        </v-btn>
        <v-btn
          v-if="lastRunAt"
          text
          :disabled="isRunning"
          @click="onSendToSentry"
        >
          <v-icon left>
            {{ icons.mdiSendCheckOutline }}
          </v-icon>
          サポートに送信
        </v-btn>
        <v-btn
          text
          color="warning"
          :disabled="isRunning"
          @click="onReloadApp"
        >
          <v-icon left>
            {{ icons.mdiRefreshAuto }}
          </v-icon>
          アプリ再読み込み
        </v-btn>
        <v-spacer />
        <v-btn
          text
          :disabled="isRunning"
          @click="onClose"
        >
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  computed, defineComponent, getCurrentInstance, ref, watch, onBeforeUnmount,
} from '@vue/composition-api'
import {
  mdiPrinterCheck, mdiClose, mdiRefresh, mdiRefreshAuto, mdiCheckCircle, mdiAlertCircle, mdiAlert, mdiSendCheckOutline, mdiAlertOctagonOutline,
} from '@mdi/js'
import usePrinterDiagnostic from '@/views/composable/usePrinterDiagnostic'

export default defineComponent({
  setup() {
    const vm = getCurrentInstance().proxy
    const {
      isOpen,
      isRunning,
      connectionResult,
      printResult,
      statusOnlyResult,
      lastRunAt,
      runDiagnostic,
      runStatusOnly,
      sendDiagnosticToSentry,
      closePrinterDiagnostic,
      formatStatusHex,
    } = usePrinterDiagnostic()

    const dialogOpen = computed({
      get: () => isOpen.value,
      set: v => {
        if (!v) closePrinterDiagnostic()
      },
    })

    // 接続が確実に OK の時だけ、テスト印刷結果やプリンタ状態を表示する
    // (接続NGの時は printer の応答自体が信頼できないため)
    const connectionOk = computed(() => !!connectionResult.value?.ok)

    const lastRunAtText = computed(() => {
      if (!lastRunAt.value) return '未実行'

      return lastRunAt.value.toLocaleString('ja-JP')
    })

    const connectionSourceText = computed(() => {
      const src = connectionResult.value?.source
      if (src === 'shared') return '共有 (receipt printer)'
      if (src === 'new') return '新規作成'

      return '—'
    })

    const latestStatusHex = computed(() => {
      const s = printResult.value?.status ?? statusOnlyResult.value?.status

      return formatStatusHex(s)
    })

    const currentChecks = computed(() => {
      if (printResult.value?.checks?.length) return printResult.value.checks
      if (statusOnlyResult.value?.checks?.length) return statusOnlyResult.value.checks

      return []
    })

    const overallColor = computed(() => {
      if (isRunning.value) return 'primary'
      if (!connectionResult.value) return 'grey'
      if (!connectionResult.value.ok) return 'error'
      if (printResult.value && !printResult.value.ok) return 'warning'

      return 'success'
    })

    const checkIcon = check => {
      if (check.ok) return mdiCheckCircle

      return check.isError ? mdiAlertCircle : mdiAlert
    }

    const checkIconColor = check => {
      if (check.ok) return 'success'

      return check.isError ? 'error' : 'warning'
    }

    // ユーザに伝える対処メッセージ（最も重要なものを1つ）
    const actionMessage = computed(() => {
      if (!connectionResult.value) return ''
      if (!connectionResult.value.ok) {
        // ① 端末がネットワーク未接続: WiFi を最優先で確認
        if (connectionResult.value.offline) {
          return 'この端末がネットワークに接続されていません。WiFi の接続先を確認してください。'
        }
        // ①.5 SSL 証明書エラーを優先表示 (期限切れ等で TLS ハンドシェイクが失敗)
        if (connectionResult.value.certError) {
          return 'プリンタのSSL証明書エラーを検知しました。設定画面の「プリンタの証明書を受け入れる（接続できない場合はこちら）」リンクを開き、「この接続ではプライバシーが保護されません」と表示されたら、左下の「詳細設定」をクリックして「（安全ではないが）アクセスする」を選んで証明書を許可してください。'
        }
        // ② プリンタ本体は到達 OK (Web設定 が応答) → 電源 OFF/ON で復旧見込み大
        if (connectionResult.value.printerReachable === true) {
          return 'プリンタとは通信できていますが、プリンタ本体が反応しなくなっています。プリンタの電源を一度切ってから入れ直してください。'
        }
        // ③ プリンタに到達できない: WiFi/プリンタ電源/LAN 確認
        if (connectionResult.value.printerReachable === false) {
          return 'プリンタに届いていません。プリンタの電源と LAN ケーブル、店舗の WiFi 接続を確認してください。'
        }

        return 'プリンタの電源とネットワーク接続を確認してください。'
      }

      const checks = currentChecks.value
      const firstError = checks.find(c => !c.ok && c.isError)
      if (firstError) {
        const messages = {
          COVER_OPEN: 'プリンタのカバーを閉じてください。',
          RECEIPT_END: 'ロール紙を交換してください。',
          OFF_LINE: 'プリンタの電源と接続を確認してください。',
          NO_RESPONSE: 'プリンタの電源と接続を確認してください。',
          AUTOCUTTER_ERR: 'カッター部の紙詰まりを確認し、プリンタを再起動してください。',
          MECHANICAL_ERR: 'プリンタを再起動してください。改善しない場合はサポートにご連絡ください。',
          UNRECOVER_ERR: 'プリンタを再起動してください。改善しない場合はサポートにご連絡ください。',
          SPOOLER_IS_STOPPED: 'プリンタを再起動してください。',
        }

        return messages[firstError.key] || '原因不明のエラーです。サポートにご連絡ください。'
      }
      const firstWarn = checks.find(c => !c.ok && !c.isError)
      if (firstWarn?.key === 'RECEIPT_NEAR_END') {
        return 'ロール紙の残量が少なくなっています。早めに準備してください。'
      }

      return ''
    })

    const onClose = () => closePrinterDiagnostic()

    const onSendToSentry = () => {
      sendDiagnosticToSentry()
      vm.$toast.success('診断結果をサポートに送信しました')
    }

    const onReloadApp = () => {
      if (window.confirm('アプリを再読み込みします。よろしいですか？')) {
        window.location.reload()
      }
    }

    // ダイアログを開いたタイミングで自動診断
    watch(isOpen, opened => {
      if (opened && !isRunning.value) {
        runDiagnostic()
      }
    })

    // 実行中の経過秒数を追跡し、長時間応答がない場合の対処ヒントを出す
    const elapsedSeconds = ref(0)
    let tickTimerId = null
    let startedAt = 0

    const clearTick = () => {
      if (tickTimerId !== null) {
        clearInterval(tickTimerId)
        tickTimerId = null
      }
    }

    watch(isRunning, running => {
      clearTick()
      elapsedSeconds.value = 0
      if (running) {
        startedAt = Date.now()
        tickTimerId = setInterval(() => {
          elapsedSeconds.value = Math.floor((Date.now() - startedAt) / 1000)
        }, 1000)
      }
    })

    onBeforeUnmount(clearTick)

    const runningMessage = computed(() => {
      if (elapsedSeconds.value < 5) return '診断を実行中...'
      if (elapsedSeconds.value < 10) return 'プリンタの応答を待っています...'

      return `応答待ち中です（${elapsedSeconds.value}秒経過）`
    })

    // 10秒以上応答がなければ「プリンタ電源OFF/ON」のヒントを表示
    const showLongWaitHint = computed(() => elapsedSeconds.value >= 10)

    return {
      dialogOpen,
      isRunning,
      runningMessage,
      showLongWaitHint,
      connectionResult,
      connectionOk,
      printResult,
      statusOnlyResult,
      lastRunAt,
      lastRunAtText,
      connectionSourceText,
      latestStatusHex,
      currentChecks,
      overallColor,
      actionMessage,
      checkIcon,
      checkIconColor,
      runDiagnostic,
      runStatusOnly,
      onClose,
      onSendToSentry,
      onReloadApp,
      icons: {
        mdiPrinterCheck, mdiClose, mdiRefresh, mdiRefreshAuto, mdiSendCheckOutline, mdiAlertOctagonOutline,
      },
    }
  },
})
</script>
